import { AppBar, Grid, makeStyles, Tab, Tabs } from '@material-ui/core';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import clsx from 'clsx';
import { ClientFields } from 'modules/clients/interfaces';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  companySelected: CompanyData | null;
  client: ClientFields | null;
  current:
    | 'configurations'
    | 'users'
    | 'applications'
    | 'sessions'
    | 'access-restrictions'
    | 'maintenance'
    | 'audit';
  permissions: PermissionsData | undefined;
  role?: RolesEnum;
}

const NavBarClients = ({
  companySelected,
  client,
  current,
  role,
  permissions,
}: Props) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  const items = [
    {
      label: i18n.t('SETTINGS_KEY'),
      slug: 'configurations',
      disabled: !!permissions && !permissions.edit_client,
    },
    {
      label: i18n.t('USERS_KEY'),
      slug: 'users',
      disabled: !!permissions && !permissions.list_users,
    },
    {
      label: i18n.t('APPLICATIONS_KEY'),
      slug: 'applications',
      disabled:
        !!permissions &&
        (client?.environment?.client_apps?.registration_source === 'platform'
          ? !permissions?.list_included_applications
          : !permissions?.list_applications),
    },
    {
      label: i18n.t('SESSIONS_KEY'),
      slug: 'sessions',
      disabled:
        !permissions?.list_environment_instance_sessions &&
        !permissions?.list_client_instance_sessions &&
        !permissions?.list_session_history,
    },
    {
      label: i18n.t('RESTRICTIONS_KEY'),
      slug: 'access-restrictions',
      disabled: !permissions?.client_access_control,
    },
    {
      label: i18n.t('MAINTENANCE_KEY'),
      slug: 'maintenance',
      disabled:
        !!permissions &&
        !permissions.get_client_maintenance &&
        !permissions?.list_client_scheduled_warnings,
      icon: client?.in_maintenance ? (
        <WarningOutlinedIcon
          color="error"
          className={classes.maintenanceIcon}
        />
      ) : (
        <></>
      ),
    },
    {
      label: i18n.t('LOGS_PAGE.LIST_LOGS_TITLE_KEY'),
      slug: 'audit',
      hidden: !!permissions && !permissions.list_all_operators_audit_logs,
    },
  ].filter(item => !item.hidden);

  return (
    <Grid item xs={12} className={classes.rootBar}>
      <AppBar
        position="static"
        color="default"
        className={clsx({
          [classes.barGreen]: inAdminView(role, companySelected),
          [classes.barGray]: inSuperAdminView(role, companySelected),
          [classes.barBlue]: inOperatorView(role, companySelected),
          [classes.barYellow]: companySelected,
        })}
      >
        <Tabs
          value={items.findIndex(item => item.slug === current)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {items.map(({ label, slug, disabled, icon }) => (
            <Tab
              disabled={disabled}
              id={`client-navbar-${slug}`}
              key={slug}
              label={label}
              icon={icon}
              onClick={() => history.push(`/clients/${id}/${slug}`)}
              className={clsx({
                [`${classes.tabGreen} ${classes.tab}`]: inAdminView(
                  role,
                  companySelected
                ),
                [`${classes.tabGray} ${classes.tab}`]: inSuperAdminView(
                  role,
                  companySelected
                ),
                [`${classes.tabBlue} ${classes.tab}`]: inOperatorView(
                  role,
                  companySelected
                ),
                [`${classes.tabYellow} ${classes.tab}`]: companySelected,
                [classes.disableCursor]: disabled,
              })}
            />
          ))}
        </Tabs>
      </AppBar>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  clients: { clientSelected },
}: ApplicationState) => ({
  client: clientSelected,
  permissions: data?.permissions,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {})(NavBarClients);
